import type { ComponentType } from 'react';
import { createBrowserRouter, Outlet, ScrollRestoration } from 'react-router';
import { PageLoader } from './components/ui';
import { RouteError } from './features/common/RouteError';
import { HomePage } from './features/marketing/HomePage';

type Module = Record<string, unknown>;

/** Lazy route: loads a page module and picks the named component. */
function page(load: () => Promise<Module>, name: string) {
  return async () => ({ Component: (await load())[name] as ComponentType });
}

const auth = () => import('./features/auth');
const admin = () => import('./features/admin');
// The lesson editor pulls in TipTap/ProseMirror; keep it out of the rest of the admin chunk.
const editor = () => import('./features/admin/editor/LessonEditorPage');
// The preview reuses the student's lesson layout, so it stays out of the admin chunk too.
const preview = () => import('./features/admin/preview/LessonPreviewPage');
const student = () => import('./features/student');
const platform = () => import('./features/platform');
const common = () => import('./features/common');

function AppRoot() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    Component: AppRoot,
    HydrateFallback: () => <PageLoader minHeight="100vh" />,
    ErrorBoundary: RouteError,
    children: [
      { path: '/', Component: HomePage },
      { path: '/login', lazy: page(auth, 'LoginPage') },
      { path: '/register', lazy: page(auth, 'RegisterPage') },
      { path: '/forgot', lazy: page(auth, 'ForgotPage') },
      { path: '/reset', lazy: page(auth, 'ResetPage') },
      { path: '/join', lazy: page(auth, 'JoinTeamPage') },
      // AI agents send the author here to consent (OAuth); signed-out authors log in first.
      { path: '/oauth', lazy: page(auth, 'RequireAuthor'), children: [{ path: 'authorize', lazy: page(auth, 'AgentConsentPage') }] },
      {
        path: '/admin',
        lazy: page(auth, 'RequireAuthor'),
        children: [
          {
            lazy: page(admin, 'AdminShell'),
            children: [
              { index: true, lazy: page(admin, 'AdminIndex') },
              { path: 'academies/new', lazy: page(admin, 'AcademyNewPage') },
              { path: 'a/:academyId', lazy: page(admin, 'AcademyPage') },
              { path: 'a/:academyId/courses/:courseId', lazy: page(admin, 'CoursePage') },
              { path: 'a/:academyId/students', lazy: page(admin, 'StudentsPage') },
              { path: 'a/:academyId/promos', lazy: page(admin, 'PromosPage') },
              { path: 'a/:academyId/marketing', lazy: page(admin, 'MarketingPage') },
              { path: 'a/:academyId/funnel', lazy: page(admin, 'FunnelPage') },
              { path: 'a/:academyId/support', lazy: page(admin, 'SupportPage') },
              { path: 'a/:academyId/team', lazy: page(admin, 'TeamPage') },
              { path: 'agents', lazy: page(admin, 'AgentsPage') },
            ],
          },
          { path: 'a/:academyId/courses/:courseId/lessons/:lessonId', lazy: page(editor, 'LessonEditorPage') },
          { path: 'a/:academyId/courses/:courseId/lessons/:lessonId/preview', lazy: page(preview, 'LessonPreviewPage') },
          {
            path: 'platform',
            lazy: page(platform, 'RequirePlatformAdmin'),
            children: [
              {
                lazy: page(platform, 'PlatformShell'),
                children: [
                  { index: true, lazy: page(platform, 'PlatformOverviewPage') },
                  { path: 'authors', lazy: page(platform, 'PlatformAuthorsPage') },
                  { path: 'academies', lazy: page(platform, 'PlatformAcademiesPage') },
                  { path: 'students', lazy: page(platform, 'PlatformStudentsPage') },
                  { path: 'plans', lazy: page(platform, 'PlatformPlansPage') },
                ],
              },
            ],
          },
        ],
      },
      { path: '/:academySlug', lazy: page(student, 'AcademyPublicPage') },
      { path: '/:academySlug/:courseSlug', lazy: page(student, 'CourseLandingPage') },
      { path: '/:academySlug/:courseSlug/learn', lazy: page(student, 'StudentCoursePage') },
      { path: '/:academySlug/:courseSlug/learn/:lessonId', lazy: page(student, 'StudentLessonPage') },
      { path: '*', lazy: page(common, 'NotFoundPage') },
    ],
  },
]);
